import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { authFetch } from "../api";
import Header from "../components/Header";
import ListingEditModal from "../components/ListingEditModal";

const OWNERSHIP_TYPES = ["CONDO", "COOP", "CONDP", "RNTL", "RENTAL", "MULTI", "TOWNHOUSE"];
const BUILDING_TYPES = ["WALKUP", "ELEVATOR", "HIGH RISE", "BROWNSTONE", "TOWNHOUSE", "LOFT", "PREWAR", "NEW DEV"];
const LISTING_STATUS_OPTIONS = [
  "ACTIVE", "BACK ON MKT", "BOARD APPROVED", "CLOSED", "CLOSED THRU US",
  "COMING SOON", "CONTRACT SIGNED", "DELETE", "DELETED", "DELISTED",
  "FUTURE", "LSE OUT", "LSE SIGN", "OFF EXCLUSIVE", "OFFER ACCEPTED",
  "PERM OFF MKT", "RENTED", "RENTED THRU US", "SOLD", "TEMP OFF MKT",
];

export default function BuildingEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [building, setBuilding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [buildingForm, setBuildingForm] = useState({});
  const [editModalListing, setEditModalListing] = useState(null);
  const [message, setMessage] = useState(null);
  const [editing, setEditing] = useState(false);
  const [tierExplain, setTierExplain] = useState(null);

  useEffect(() => {
    if (!localStorage.getItem("token")) navigate("/login");
  }, [navigate]);

  const loadBuilding = async () => {
    try {
      const data = await authFetch(`/buildings/${id}`);
      setBuilding(data);
      setBuildingForm({
        total_units: data.total_units,
        total_floors: data.total_floors,
        year_built: data.year_built,
        year_renovated: data.year_renovated,
        ownership_type: data.ownership_type || "",
        building_type: data.building_type || "",
        building_tier: data.building_tier || "",
        doorman: data.doorman,
        elevator_in_building: data.elevator,
        concierge: data.concierge,
        laundry: data.laundry,
        storage: data.storage,
        healthclub: data.healthclub,
        pool: data.pool,
        courtyard: data.courtyard,
        roof_deck: data.roof_deck,
        all_units_washer_dryer: data.all_units_washer_dryer || false,
        all_units_dishwasher: data.all_units_dishwasher || false,
      });
      setLoading(false);
      authFetch(`/buildings/${id}/tier-explanation`)
        .then((t) => setTierExplain(t && !t.detail ? t : null))
        .catch(() => setTierExplain(null));
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  useEffect(() => { loadBuilding(); }, [id]);

  const saveBuilding = async () => {
    setSaving(true);
    try {
      await authFetch(`/buildings/${id}`, {
        method: "PATCH",
        body: JSON.stringify(buildingForm),
      });
      setMessage({ type: "success", text: "Building saved" });
      loadBuilding();
    } catch (err) {
      setMessage({ type: "error", text: err.message });
    }
    setSaving(false);
    setTimeout(() => setMessage(null), 3000);
  };

  // Open the standard app-wide ListingEditModal (same as ListingsPage/PredictionPage).
  // Pass id-only (NOT the building's summary listing row) so the modal fetches the full
  // canonical /listings/{id}/detail shape. The summary row has `bedrooms` but lacks
  // listing_url/acris_url/block/lot/listing_details, and the modal skips its /detail fetch
  // when bedrooms is present — which left those fields blank (e.g. the Listing URL field
  // looked like it never saved). Header context is still enriched for the loading state.
  const startEditListing = (listing) => {
    setEditModalListing({
      id: listing.id,
      address: building?.display_address,
      property_id: building?.id,
      neighborhood: building?.neighborhood,
      condo_coop: building?.ownership_type,
      building_tier: building?.building_tier,
    });
  };

  const approveCoop = async () => {
    setSaving(true);
    try {
      await authFetch(`/buildings/${id}`, {
        method: "PATCH",
        body: JSON.stringify({}),
      });
      // Use the approve endpoint logic directly
      const resp = await fetch(`${process.env.REACT_APP_API_URL}/buildings/approve-coops`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}`, "Content-Type": "multipart/form-data" },
        body: (() => {
          const csv = `address,zipcode\n${building.display_address},${building.postal_code}`;
          const blob = new Blob([csv], { type: "text/csv" });
          const fd = new FormData();
          fd.append("file", blob, "approve.csv");
          return fd;
        })(),
      });
      const data = await resp.json();
      setMessage({ type: "success", text: `Co-op approved: ${data.listings_approved || 0} listings moved to review` });
      loadBuilding();
    } catch (err) {
      setMessage({ type: "error", text: err.message });
    }
    setSaving(false);
  };

  if (loading) return (
    <div><Header showLogout={true} />
      <div style={{ padding: "40px", textAlign: "center" }}>Loading...</div>
    </div>
  );

  if (!building) return (
    <div><Header showLogout={true} />
      <div style={{ padding: "40px", textAlign: "center" }}>Building not found</div>
    </div>
  );

  const inputStyle = { padding: "6px 10px", border: "1px solid #ddd", borderRadius: "4px", fontSize: "13px", width: "100%" };
  const selectStyle = { ...inputStyle, backgroundColor: "#fff" };
  const labelStyle = { fontSize: "11px", fontWeight: "600", color: "#555", marginBottom: "2px", display: "block" };
  const sectionStyle = { backgroundColor: "#fff", padding: "20px", borderRadius: "8px", marginBottom: "16px", boxShadow: "0 2px 4px rgba(0,0,0,0.08)" };
  const gridStyle = { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: "12px" };
  const boolStyle = (val) => ({
    padding: "4px 10px", borderRadius: "4px", fontSize: "12px", fontWeight: "600", cursor: "pointer", border: "1px solid #ddd",
    backgroundColor: val ? "#e8f5e9" : "#f5f5f5", color: val ? "#2e7d32" : "#999",
  });

  const isCoop = building.ownership_type === "COOP" || building.ownership_type === "CONDP";
  const isApproved = building.coop_policies?.approved;

  // Group listings by unit
  const unitGroups = {};
  (building.listings || []).forEach((l) => {
    const key = l.unit_number || "—";
    if (!unitGroups[key]) unitGroups[key] = [];
    unitGroups[key].push(l);
  });

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#f5f5f5" }}>
      <Header showLogout={true} />
      <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "20px" }}>
        {/* Breadcrumb */}
        <div style={{ marginBottom: "16px", fontSize: "13px" }}>
          <Link to="/buildings" style={{ color: "#1976d2", textDecoration: "none" }}>Buildings</Link>
          {" → "}
          <span>{building.display_address}</span>
        </div>

        {/* Message */}
        {message && (
          <div style={{
            padding: "10px 16px", borderRadius: "6px", marginBottom: "16px", fontSize: "13px",
            backgroundColor: message.type === "success" ? "#e8f5e9" : "#ffebee",
            color: message.type === "success" ? "#2e7d32" : "#c62828",
          }}>
            {message.text}
          </div>
        )}

        {/* Building Header */}
        <div style={{ ...sectionStyle, borderLeft: "4px solid #1976d2" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
            <div>
              <h1 style={{ margin: 0, fontSize: "22px" }}>{building.display_address}</h1>
              <div style={{ fontSize: "13px", color: "#666", marginTop: "4px" }}>
                {building.neighborhood} · {building.postal_code} · ID: {building.id}
                {building.se_url && (
                  <a href={building.se_url} target="_blank" rel="noreferrer" style={{ marginLeft: "12px", color: "#1976d2" }}>SE ↗</a>
                )}
              </div>
            </div>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              {isCoop && !isApproved && (
                <button onClick={approveCoop} disabled={saving}
                  style={{ padding: "8px 16px", backgroundColor: "#4caf50", color: "#fff", border: "none", borderRadius: "6px", fontSize: "13px", fontWeight: "600", cursor: "pointer" }}>
                  Approve Co-op
                </button>
              )}
              {isApproved && (
                <span style={{ padding: "6px 12px", backgroundColor: "#e8f5e9", color: "#2e7d32", borderRadius: "6px", fontSize: "12px", fontWeight: "600" }}>
                  Co-op Approved
                </span>
              )}
              {!editing ? (
                <button onClick={() => setEditing(true)}
                  style={{ padding: "8px 16px", backgroundColor: "#fff", color: "#333", border: "1px solid #ccc", borderRadius: "6px", fontSize: "13px", cursor: "pointer" }}>
                  &#9998; Edit
                </button>
              ) : (
                <>
                  <button onClick={() => { saveBuilding(); setEditing(false); }} disabled={saving}
                    style={{ padding: "8px 16px", backgroundColor: "#1976d2", color: "#fff", border: "none", borderRadius: "6px", fontSize: "13px", fontWeight: "600", cursor: "pointer" }}>
                    {saving ? "Saving..." : "Save"}
                  </button>
                  <button onClick={() => { setEditing(false); loadBuilding(); }}
                    style={{ padding: "8px 16px", backgroundColor: "#fff", color: "#666", border: "1px solid #ccc", borderRadius: "6px", fontSize: "13px", cursor: "pointer" }}>
                    Cancel
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Building Fields */}
          <div style={{ ...gridStyle, opacity: editing ? 1 : 0.7, pointerEvents: editing ? "auto" : "none" }}>
            <div><label style={labelStyle}>Total Units</label>
              <input type="number" value={buildingForm.total_units ?? ""} onChange={(e) => setBuildingForm({ ...buildingForm, total_units: e.target.value ? parseInt(e.target.value) : null })} style={inputStyle} /></div>
            <div><label style={labelStyle}>Total Floors</label>
              <input type="number" value={buildingForm.total_floors ?? ""} onChange={(e) => setBuildingForm({ ...buildingForm, total_floors: e.target.value ? parseInt(e.target.value) : null })} style={inputStyle} /></div>
            <div><label style={labelStyle}>Year Built</label>
              <input type="number" value={buildingForm.year_built ?? ""} onChange={(e) => setBuildingForm({ ...buildingForm, year_built: e.target.value ? parseInt(e.target.value) : null })} style={inputStyle} /></div>
            <div><label style={labelStyle}>Year Renovated</label>
              <input type="number" value={buildingForm.year_renovated ?? ""} onChange={(e) => setBuildingForm({ ...buildingForm, year_renovated: e.target.value ? parseInt(e.target.value) : null })} style={inputStyle} /></div>
            <div><label style={labelStyle}>Ownership</label>
              <select value={buildingForm.ownership_type} onChange={(e) => setBuildingForm({ ...buildingForm, ownership_type: e.target.value })} style={selectStyle}>
                <option value="">—</option>
                {OWNERSHIP_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select></div>
            <div><label style={labelStyle}>Building Type</label>
              <select value={buildingForm.building_type} onChange={(e) => setBuildingForm({ ...buildingForm, building_type: e.target.value })} style={selectStyle}>
                <option value="">—</option>
                {BUILDING_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select></div>
            <div><label style={labelStyle}>Building Tier</label>
              <select value={buildingForm.building_tier} onChange={(e) => setBuildingForm({ ...buildingForm, building_tier: e.target.value })} style={inputStyle}>
                <option value="">-</option>
                <option value="Walk up">Walk up</option>
                <option value="Walk up plus">Walk up plus</option>
                <option value="E only">E only</option>
                <option value="E plus">E plus</option>
                <option value="E/D only">E/D only</option>
                <option value="Low">Low</option>
                <option value="Med">Med</option>
                <option value="High">High</option>
                <option value="Luxury">Luxury</option>
              </select>
              {tierExplain && (
                <div style={{ marginTop: "6px", fontSize: "11px", color: "#555", background: "#f7f9fa", border: "1px solid #eceff1", borderRadius: "6px", padding: "7px 9px", lineHeight: 1.5 }}>
                  <div style={{ fontWeight: 600, color: "#333", marginBottom: "2px" }}>How this tier was derived</div>
                  <div>{tierExplain.reason}</div>
                  {tierExplain.human_set ? (
                    <div style={{ marginTop: "3px", color: "#2e7d32" }}>
                      Stored tier <b>{tierExplain.current_tier}</b> was set manually — kept, not auto-recomputed.
                    </div>
                  ) : tierExplain.computed_tier !== tierExplain.current_tier ? (
                    <div style={{ marginTop: "3px", color: "#e65100" }}>
                      Stored <b>{tierExplain.current_tier}</b> differs from computed <b>{tierExplain.computed_tier}</b>.
                    </div>
                  ) : null}
                </div>
              )}
              </div>
          </div>

          {/* Amenities */}
          <div style={{ marginTop: "16px", opacity: editing ? 1 : 0.7, pointerEvents: editing ? "auto" : "none" }}>
            <label style={{ ...labelStyle, marginBottom: "8px" }}>Amenities</label>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              {[
                ["doorman", "Doorman"], ["elevator_in_building", "Elevator"], ["concierge", "Concierge"],
                ["laundry", "Laundry"], ["storage", "Storage"], ["healthclub", "Gym"],
                ["pool", "Pool"], ["courtyard", "Courtyard"], ["roof_deck", "Roof Deck"],
              ].map(([key, label]) => (
                <button key={key} style={boolStyle(buildingForm[key])}
                  onClick={() => setBuildingForm({ ...buildingForm, [key]: !buildingForm[key] })}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Unit-level amenity flags */}
          <div style={{ marginTop: "16px", opacity: editing ? 1 : 0.7, pointerEvents: editing ? "auto" : "none" }}>
            <label style={{ ...labelStyle, marginBottom: "8px" }}>Unit Amenities (all units)</label>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              {[
                ["all_units_washer_dryer", "All units have W/D"],
                ["all_units_dishwasher", "All units have D/W"],
              ].map(([key, label]) => (
                <button key={key} style={boolStyle(buildingForm[key])}
                  onClick={() => {
                    const newVal = !buildingForm[key];
                    if (newVal) {
                      const confirmed = window.confirm(
                        `Are you sure? This will mark ALL listings in this building as having ${key === "all_units_washer_dryer" ? "washer/dryer" : "dishwasher"}.\n\nThis will update all existing units immediately.`
                      );
                      if (!confirmed) return;
                      // Save immediately with the flag
                      setBuildingForm({ ...buildingForm, [key]: true });
                      authFetch(`/buildings/${id}`, {
                        method: "PATCH",
                        body: JSON.stringify({ [key]: true }),
                      }).then((res) => {
                        setMessage({ type: "success", text: res.message || "Updated" });
                        loadBuilding();
                      }).catch((err) => {
                        setMessage({ type: "error", text: err.message });
                        setBuildingForm({ ...buildingForm, [key]: false });
                      });
                      setTimeout(() => setMessage(null), 3000);
                    } else {
                      window.alert(
                        `Unchecking this flag will NOT update existing units.\n\nTo remove ${key === "all_units_washer_dryer" ? "W/D" : "D/W"} from individual units, please edit them manually.`
                      );
                      setBuildingForm({ ...buildingForm, [key]: false });
                      authFetch(`/buildings/${id}`, {
                        method: "PATCH",
                        body: JSON.stringify({ [key]: false }),
                      }).then(() => loadBuilding())
                        .catch((err) => setMessage({ type: "error", text: err.message }));
                    }
                  }}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Stats */}
          <div style={{ marginTop: "16px", display: "flex", gap: "16px", fontSize: "13px", color: "#666" }}>
            <span>{building.active_sale_listings} active sales</span>
            <span>{building.active_rental_listings} active rentals</span>
            {building.zone_name && <span>Zone: {building.zone_name} ({building.zone_score})</span>}
            {building.dist_to_subway != null && <span>Subway: {building.dist_to_subway.toFixed(2)} mi</span>}
          </div>
        </div>

        {/* Units — SE-style Available / Unavailable */}
        <BuildingUnitsSection
          listings={building.listings || []}
          buildingId={id}
          startEditListing={startEditListing}
        />
      </div>

      {editModalListing && (
        <ListingEditModal
          listing={editModalListing}
          onClose={() => setEditModalListing(null)}
          onSave={() => { setEditModalListing(null); loadBuilding(); }}
          onVerify={() => { setEditModalListing(null); loadBuilding(); }}
        />
      )}
    </div>
  );
}

const ACTIVE_STATUSES = ['ACTIVE', 'BACK ON MKT', 'COMING SOON', 'TEMP OFF MKT', 'OFF EXCLUSIVE', 'CONTRACT OUT', 'OFFER ACCEPTED', 'BOARD APPROVED', 'LSE OUT', 'APP IN', 'APP ACCEPTED'];
const UNAVAILABLE_STATUSES = ['RENTED', 'RENTED THRU US', 'CLOSED', 'CLOSED THRU US', 'SOLD', 'LSE SIGN', 'CONTRACT SIGNED', 'PERM OFF MKT', 'DELISTED'];
const HIDDEN_STATUSES = ['DELETED', 'DELETE', 'NOT RENTED', 'FUTURE'];

function BuildingUnitsSection({ listings, buildingId, startEditListing }) {
  const [availTab, setAvailTab] = useState("available");
  const [typeFilter, setTypeFilter] = useState("all"); // "all", "S", "R"
  const [bedFilter, setBedFilter] = useState("all");
  const [sortField, setSortField] = useState("date");
  const [sortDir, setSortDir] = useState("desc");

  const available = listings.filter(l => ACTIVE_STATUSES.includes(l.listing_status) && l.internal_status !== 'DELETED');

  // Unavailable: filter $0 prices and deduplicate by unit + date + price
  const unavailableRaw = listings.filter(l => UNAVAILABLE_STATUSES.includes(l.listing_status) && (l.list_price || l.closing_price));
  const seenKeys = new Set();
  const unavailable = unavailableRaw.filter(l => {
    const key = `${(l.unit_number || "").toUpperCase()}_${l.closing_date || l.created_date}_${l.closing_price || l.list_price}`;
    if (seenKeys.has(key)) return false;
    seenKeys.add(key);
    return true;
  });

  const current = availTab === "available" ? available : unavailable;

  const filtered = current.filter(l => {
    if (typeFilter !== "all" && l.sale_or_rental !== typeFilter) return false;
    if (bedFilter !== "all") {
      const b = parseInt(bedFilter);
      if (b === 4) { if ((l.bedrooms || 0) < 4) return false; }
      else if ((l.bedrooms || 0) !== b) return false;
    }
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    let va, vb;
    switch (sortField) {
      case "date": va = a.closing_date || a.created_date || ""; vb = b.closing_date || b.created_date || ""; break;
      case "unit": va = a.unit_number || ""; vb = b.unit_number || ""; break;
      case "price": va = a.closing_price || a.list_price || 0; vb = b.closing_price || b.list_price || 0; break;
      case "beds": va = a.bedrooms || 0; vb = b.bedrooms || 0; break;
      case "baths": va = a.bathrooms || 0; vb = b.bathrooms || 0; break;
      case "size": va = a.sqft || 0; vb = b.sqft || 0; break;
      default: va = ""; vb = "";
    }
    if (va < vb) return sortDir === "asc" ? -1 : 1;
    if (va > vb) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortField(field); setSortDir("desc"); }
  };

  const sortIcon = (field) => sortField === field ? (sortDir === "asc" ? " ▲" : " ▼") : "";

  const bedCounts = {};
  current.forEach(l => {
    const b = l.bedrooms === 0 ? 0 : (l.bedrooms || 0);
    bedCounts[b] = (bedCounts[b] || 0) + 1;
  });

  const formatDate = (iso) => {
    if (!iso) return "-";
    try { return new Date(iso).toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "2-digit" }); }
    catch { return iso.slice(0, 10); }
  };

  const formatStatus = (l) => {
    if (l.listing_status === 'CLOSED' || l.listing_status === 'SOLD' || l.listing_status === 'CLOSED THRU US') {
      const closing = l.closing_price || l.list_price;
      const asking = l.original_price || l.list_price;
      if (closing && asking && closing !== asking) {
        const pct = ((closing - asking) / asking * 100).toFixed(2);
        return `Sold (asking: $${asking.toLocaleString()}; ${pct}%)`;
      }
      return l.listing_status;
    }
    if (l.listing_status === 'PERM OFF MKT') return 'No longer available';
    if (l.listing_status === 'DELISTED') return 'Delisted';
    if (l.listing_status === 'RENTED' || l.listing_status === 'RENTED THRU US') return 'Rented';
    if (l.listing_status === 'LSE SIGN') return 'Lease signed';
    if (l.listing_status === 'CONTRACT SIGNED') return 'In contract';
    return l.listing_status;
  };

  const sectionStyle = { background: "#fff", border: "1px solid #e0e0e0", borderRadius: "8px", padding: "20px", marginBottom: "20px" };
  const thStyle = { textAlign: "left", padding: "8px 10px", fontSize: "12px", fontWeight: 600, color: "#666", cursor: "pointer", whiteSpace: "nowrap", borderBottom: "2px solid #eee" };
  const tdStyle = { padding: "8px 10px", fontSize: "13px", borderBottom: "1px solid #f0f0f0" };

  return (
    <div style={sectionStyle}>
      {/* Available / Unavailable toggle */}
      <div style={{ display: "flex", gap: 0, marginBottom: "16px", borderBottom: "2px solid #e0e0e0" }}>
        {[
          { key: "available", label: `Available (${available.length})` },
          { key: "unavailable", label: `Unavailable (${unavailable.length})` },
        ].map(tab => (
          <button key={tab.key} onClick={() => { setAvailTab(tab.key); setBedFilter("all"); }}
            style={{
              padding: "10px 24px", fontSize: "14px", fontWeight: availTab === tab.key ? 600 : 400,
              color: availTab === tab.key ? "#1976d2" : "#666", backgroundColor: "transparent",
              border: "none", borderBottom: availTab === tab.key ? "2px solid #1976d2" : "2px solid transparent",
              marginBottom: "-2px", cursor: "pointer",
            }}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Sale / Rent toggle */}
      <div style={{ display: "flex", gap: 0, marginBottom: "12px" }}>
        {[
          { key: "all", label: "All" },
          { key: "S", label: "For sale" },
          { key: "R", label: "For rent" },
        ].map(t => (
          <button key={t.key} onClick={() => setTypeFilter(t.key)}
            style={{
              padding: "6px 16px", fontSize: "13px", fontWeight: typeFilter === t.key ? 600 : 400,
              backgroundColor: typeFilter === t.key ? "#1976d2" : "#fff",
              color: typeFilter === t.key ? "#fff" : "#333",
              border: "1px solid #ccc", cursor: "pointer",
              borderRadius: t.key === "all" ? "4px 0 0 4px" : t.key === "R" ? "0 4px 4px 0" : "0",
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Bedroom filter tabs */}
      <div style={{ display: "flex", gap: "4px", marginBottom: "16px", fontSize: "13px" }}>
        <span onClick={() => setBedFilter("all")}
          style={{ padding: "4px 12px", cursor: "pointer", fontWeight: bedFilter === "all" ? 600 : 400, borderBottom: bedFilter === "all" ? "2px solid #000" : "none" }}>
          All ({current.filter(l => typeFilter === "all" || l.sale_or_rental === typeFilter).length})
        </span>
        {[0, 1, 2, 3, 4].map(b => {
          const label = b === 0 ? "Studio" : b === 4 ? "4+ beds" : `${b} bed${b > 1 ? "s" : ""}`;
          const count = current.filter(l => {
            if (typeFilter !== "all" && l.sale_or_rental !== typeFilter) return false;
            if (b === 4) return (l.bedrooms || 0) >= 4;
            return (l.bedrooms || 0) === b;
          }).length;
          if (count === 0) return null;
          return (
            <span key={b} onClick={() => setBedFilter(String(b))}
              style={{ padding: "4px 12px", cursor: "pointer", fontWeight: bedFilter === String(b) ? 600 : 400, borderBottom: bedFilter === String(b) ? "2px solid #000" : "none" }}>
              {label} ({count})
            </span>
          );
        })}
      </div>

      {/* Table */}
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={thStyle} onClick={() => toggleSort("date")}>{availTab === "available" ? "Listed" : "Off-market date"}{sortIcon("date")}</th>
            <th style={thStyle} onClick={() => toggleSort("unit")}>Unit{sortIcon("unit")}</th>
            <th style={thStyle} onClick={() => toggleSort("price")}>Price{sortIcon("price")}</th>
            <th style={thStyle}>Status</th>
            <th style={thStyle} onClick={() => toggleSort("beds")}>Beds{sortIcon("beds")}</th>
            <th style={thStyle} onClick={() => toggleSort("baths")}>Baths{sortIcon("baths")}</th>
            <th style={thStyle} onClick={() => toggleSort("size")}>Size{sortIcon("size")}</th>
            <th style={thStyle}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 ? (
            <tr><td colSpan={8} style={{ ...tdStyle, textAlign: "center", color: "#999", padding: "24px" }}>No listings</td></tr>
          ) : sorted.map(l => (
            <tr key={l.id} style={{ cursor: "pointer" }} onClick={() => startEditListing(l)}>
              <td style={tdStyle}>{formatDate(availTab === "available" ? l.created_date : (l.closing_date || l.created_date))}</td>
              <td style={{ ...tdStyle, fontWeight: 600, color: "#1976d2" }}>#{l.unit_number || "?"}</td>
              <td style={{ ...tdStyle, fontWeight: 500 }}>${(l.closing_price || l.list_price || 0).toLocaleString()}{l.sale_or_rental === "R" ? "/mo" : ""}</td>
              <td style={{ ...tdStyle, color: availTab === "available" ? "#4caf50" : "#666" }}>{formatStatus(l)}</td>
              <td style={tdStyle}>{l.bedrooms === 0 ? "Studio" : `${l.bedrooms} bed`}</td>
              <td style={tdStyle}>{l.bathrooms} bath</td>
              <td style={tdStyle}>{l.sqft ? `${l.sqft.toLocaleString()} ft²` : "-"}</td>
              <td style={tdStyle} onClick={e => e.stopPropagation()}>
                <div style={{ display: "flex", gap: "6px" }}>
                  {l.se_url && <a href={l.se_url} target="_blank" rel="noreferrer" style={{ fontSize: "11px", color: "#1976d2" }}>SE</a>}
                  {l.sale_or_rental === "S" && <a href={`/predict/${l.id}`} target="_blank" rel="noreferrer" style={{ fontSize: "11px", color: "#1976d2" }}>Analysis</a>}
                  <span onClick={() => startEditListing(l)} style={{ fontSize: "11px", cursor: "pointer", color: "#1976d2" }}>Edit</span>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
